import {useEffect} from "react";
import {OverlayTrigger, Popover} from "react-bootstrap";
import {useFormContext} from "react-hook-form";

import {Button} from "@/components/button/Button.tsx";
import {InputField} from "@/components/form/fields/InputField/InputField.tsx";
import {PasswordField} from "@/components/form/fields/PasswordField/PasswordField.tsx";
import {SelectField} from "@/components/form/fields/SelectField/SelectField.tsx";
import {SwitchField} from "@/components/form/fields/SwitchField.tsx";
import {TextAreaField} from "@/components/form/fields/TextAreaField.tsx";
import {ServerErrors} from "@/lib/types/common.ts";
import {IntegrationFormData} from "@/lib/types/integrations.ts";
import {SetupInfo} from "@/pages/integrations/edit/subForms/components/SetupInfo.tsx";

import styles from "@/pages/integrations/edit/subForms/ServiceNowForm.module.scss";

export type FormValuesKey = keyof IntegrationFormData;

interface ServiceNowFormProps {
  serverErrors?: ServerErrors;
}

const URGENCY_OPTIONS = [
  {value: "", label: "Default (none)"},
  {value: "1", label: "1 - High"},
  {value: "2", label: "2 - Medium"},
  {value: "3", label: "3 - Low"},
];

const IMPACT_OPTIONS = [
  {value: "", label: "Default (none)"},
  {value: "1", label: "1 - High"},
  {value: "2", label: "2 - Medium"},
  {value: "3", label: "3 - Low"},
];

export const ServiceNowForm = ({serverErrors}: ServiceNowFormProps) => {
  const {setError} = useFormContext();

  useEffect(() => {
    if (serverErrors) {
      Object.keys(serverErrors)
        .filter((k) => k !== "__all__")
        .forEach((key) => setError(`settings.${key}` as FormValuesKey, {message: serverErrors[key][0]}));
    }
  }, [setError, serverErrors]);

  const customFieldExample = (
    <Popover id="servicenow-custom-fields-example" className={styles.examplePopover}>
      <Popover.Header as="h3">Example Configuration</Popover.Header>
      <Popover.Body>
        <pre>{`{
  "category": "Application",
  "subcategory": "Error Message",
  "business_service": "c4ca4238a0b923820dcc509a6f75849",
  "service_offering": "c81e728d9d4c2f636f067f89cc14862c",
  "cmdb_ci": "eccbc87e4b5ce2fe28308fd9f2a7baf3",
  "contact_type": "Monitoring",
  "assignment_group": "Monitoring System-L2",
  "u_check_name": "$service.display_name",
  "u_check_url": "$service.msp_address",
  "u_check_type": "$service.monitoring_service_type_display",
  "u_check_tags": "$service.tags",
  "u_locations_down": "$global_alert_state.num_locations_down",
  "u_failing_locations": "$locations",
  "u_alert_output": "$alert.short_output",
  "u_account_name": "$account.name",
  "u_monitoring_tool": "Uptime.com",
  "u_summary": "$service.display_name is down ($service.msp_address)"
}`}</pre>
        <div>
          Any <code>$reference</code> within a value is replaced with the matching value from the Alert, so a value can
          combine several of them with text of your own. Anything else is sent as-is. To send a literal <code>$</code>{" "}
          followed by a letter, double it: <code>$$service</code>. A mapped <code>assignment_group</code> is looked up
          by name the same way as the dedicated Assignment Group setting. Other reference fields, such as{" "}
          <code>business_service</code>, <code>service_offering</code> and <code>cmdb_ci</code>, are not looked up this
          way and need their target record&apos;s sys_id, not its display name.
        </div>
      </Popover.Body>
    </Popover>
  );

  return (
    <>
      <div className="row row-cols-2 mb-3">
        <div className="col-6 mb-3">
          <InputField
            name="settings.instance_url"
            label="Instance URL"
            required={true}
            tooltip='The base URL of your ServiceNow instance, e.g. "https://your-instance.service-now.com"'
            autoFocus={true}
          />
        </div>
        <div className="col-6 mb-3">
          <InputField
            name="settings.assignment_group"
            label="Assignment Group"
            required={false}
            tooltip="Name or sys_id of the group incidents should be assigned to."
          />
        </div>
      </div>
      <div className="row row-cols-2 mb-3">
        <div className="col-6 mb-3">
          <InputField
            name="settings.username"
            label="Username"
            required={true}
            tooltip="User ID of the ServiceNow service account used by this integration."
          />
        </div>
        <div className="col-6 mb-3">
          <PasswordField
            name="settings.password"
            label="Password"
            required={true}
            tooltip="Password of the ServiceNow service account."
          />
        </div>
      </div>
      <div className="row row-cols-2 mb-3">
        <div className="col-6 mb-3">
          <SelectField
            name="settings.urgency"
            label="Urgency"
            required={false}
            options={URGENCY_OPTIONS}
            tooltip="Urgency to assign to incidents created by this integration."
          />
        </div>
        <div className="col-6 mb-3">
          <SelectField
            name="settings.impact"
            label="Impact"
            required={false}
            options={IMPACT_OPTIONS}
            tooltip="Impact to assign to incidents created by this integration."
          />
        </div>
      </div>
      <div className="row row-cols-2 mb-3">
        <div className="col-6 mb-3">
          <InputField
            name="settings.close_code"
            label="Resolution Code"
            required={false}
            tooltip="Resolution code applied when auto-resolving an incident. Must match one of the resolution codes configured on your instance."
          />
        </div>
        <div className="col-6 mb-3">
          <SwitchField
            name="settings.autoresolve"
            label="Auto-resolve Incident"
            tooltip="Automatically resolve the incident once the Check is back up."
          />
        </div>
      </div>
      <div className="row mb-3">
        <div className="col-12 mb-3">
          <SwitchField
            name="settings.include_payload"
            label="Include Full Alert Payload"
            tooltip="Attach the complete Alert details as JSON to the Incident's Work notes, so they are available to reports, flows and business rules on your instance."
          />
        </div>
        <div className="col-12 mb-3">
          <TextAreaField
            rows={6}
            name="settings.custom_fields_json"
            label="Additional Fields"
            required={false}
            tooltip="Advanced configuration for additional Incident fields, built-in or custom, using JSON format."
            className={styles.codeArea}
          />
          <OverlayTrigger trigger="click" placement="right" overlay={customFieldExample}>
            <Button variant="linkBlue" size="text" type="button">
              See example configuration
            </Button>
          </OverlayTrigger>
        </div>
      </div>
      <SetupInfo>
        <div className="p-2">
          This integration creates an Incident in your ServiceNow instance when an Alert is raised, and resolves the
          Incident when the Alert is cleared.
        </div>
        <div className="p-2">
          <ol className="px-3">
            <li>
              <p className="mb-0 pulseTextBodyBold">Create a ServiceNow service account</p>
              <p className="mb-1">
                In ServiceNow, go to <span className="pulseTextBodyBold">User Administration &gt; Users &gt; New</span>{" "}
                and create a dedicated account for this integration. Set{" "}
                <span className="pulseTextBodyBold">Identity type</span> to &quot;Machine&quot; (on releases prior to
                Yokohama, check &quot;Web service access only&quot; instead), then set a password for the account and
                enter the credentials above.
              </p>
            </li>
            <li>
              <p className="mb-0 pulseTextBodyBold">Grant the required roles</p>
              <p className="mb-1">
                On the user record, open the <span className="pulseTextBodyBold">Roles</span> related list and add{" "}
                <code>snc_platform_rest_api_access</code> and <code>itil</code>.
              </p>
            </li>
            <li>
              <p className="mb-0 pulseTextBodyBold">Determine your Instance URL</p>
              <p className="mb-1">
                Enter the base URL of your ServiceNow instance into the{" "}
                <span className="pulseTextBodyBold">Instance URL</span> field, e.g.{" "}
                <code>https://your-instance.service-now.com</code>.
              </p>
            </li>
            <li>
              <p className="mb-0 pulseTextBodyBold">Map additional fields (optional)</p>
              <p className="mb-1">
                The full Alert details are attached to each Incident&apos;s{" "}
                <span className="pulseTextBodyBold">Work notes</span> as JSON. To put individual values into the
                Incident&apos;s own columns instead, map them in{" "}
                <span className="pulseTextBodyBold">Additional Fields</span> above — built-in columns such as{" "}
                <code>category</code> or <code>business_service</code> work directly. For a value with no matching
                built-in column, go to <span className="pulseTextBodyBold">System Definition &gt; Tables</span>, open
                the <code>incident</code> table and add a custom column (ServiceNow prefixes these with <code>u_</code>
                ), then map it the same way. A few fields this integration manages itself, such as <code>
                  state
                </code>{" "}
                and correlation fields, can&apos;t be set here.
              </p>
            </li>
          </ol>
        </div>
      </SetupInfo>
    </>
  );
};
